/**
 * DevicePalette — Collapsible right-side panel listing IoT devices.
 *
 * Devices can be dragged onto infrastructure objects on the map to create
 * has_device relationship edges (dual-write pattern).
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Search, Cpu, Radio, Satellite, Router,
  GripVertical, ChevronDown, Loader2,
} from 'lucide-react';
import { useMapStore } from '../MapContext';
import { useIoTDevices, useLinkInfraDevice } from '../api';
import { toast } from 'sonner';

/* ------------------------------------------------------------------ */
/*  Device type icons                                                   */
/* ------------------------------------------------------------------ */

const DEVICE_TYPE_ICONS: Record<string, typeof Cpu> = {
  sensor: Radio,
  gateway: Router,
  tracker: Satellite,
  default: Cpu,
};

function getDeviceIcon(type: string) {
  const normalized = (type || '').toLowerCase();
  if (normalized.includes('gateway')) return Router;
  if (normalized.includes('sensor') || normalized.includes('probe')) return Radio;
  if (normalized.includes('track') || normalized.includes('gps')) return Satellite;
  return Cpu;
}

/* ------------------------------------------------------------------ */
/*  Device Item                                                         */
/* ------------------------------------------------------------------ */

function DeviceItem({
  device,
  onLink,
}: {
  device: { deviceId: string; type: string; status: string; manufacturer?: string; model?: string };
  onLink: (deviceId: string) => void;
}) {
  const Icon = getDeviceIcon(device.type);
  const isOnline = device.status === 'online' || device.status === 'active';

  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.dataTransfer.setData('application/x-iot-device-id', device.deviceId);
    e.dataTransfer.setData('text/plain', device.deviceId);
    e.dataTransfer.effectAllowed = 'link';
  }, [device.deviceId]);

  return (
    <motion.div
      layout
      draggable
      onDragStart={handleDragStart}
      className="group flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-gray-50 cursor-grab active:cursor-grabbing transition-colors"
    >
      <GripVertical className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${isOnline ? 'bg-emerald-50' : 'bg-gray-100'}`}>
        <Icon className={`w-3.5 h-3.5 ${isOnline ? 'text-emerald-600' : 'text-gray-400'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-gray-800 truncate font-mono">{device.deviceId}</div>
        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOnline ? 'bg-green-500' : 'bg-gray-300'}`} />
          <span className="text-[10px] text-gray-400 truncate">
            {device.manufacturer && device.model ? `${device.manufacturer} ${device.model}` : device.type}
          </span>
        </div>
      </div>
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={(e) => { e.stopPropagation(); onLink(device.deviceId); }}
        className="p-1 rounded-md text-gray-300 hover:text-blue-600 hover:bg-blue-50 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
        title="Link to selected infrastructure"
      >
        <Cpu className="w-3 h-3" />
      </motion.button>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                      */
/* ------------------------------------------------------------------ */

export default function DevicePalette() {
  const { devicePaletteOpen, setDevicePaletteOpen, selectedInfrastructure } = useMapStore();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const { data: devices, isLoading } = useIoTDevices({ q: search, type: typeFilter, limit: 100 });
  const linkDevice = useLinkInfraDevice();

  const handleLink = useCallback((deviceId: string) => {
    if (!selectedInfrastructure) {
      toast.warning('Select an infrastructure object first');
      return;
    }
    linkDevice.mutate(
      { infraId: selectedInfrastructure.id, device_id: deviceId },
      {
        onSuccess: () => toast.success(`Device ${deviceId} linked`),
        onError: (err) => toast.error('Link failed', { description: String(err) }),
      }
    );
  }, [selectedInfrastructure, linkDevice]);

  // Group devices by type
  const grouped = (devices ?? []).reduce<Record<string, typeof devices>>((acc, d) => {
    const group = d.type || 'other';
    if (!acc[group]) acc[group] = [];
    acc[group]!.push(d);
    return acc;
  }, {});

  return (
    <AnimatePresence>
      {devicePaletteOpen && (
        <motion.div
          initial={{ x: 320, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 320, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="absolute right-0 top-0 w-72 h-full bg-white/95 backdrop-blur-md border-l border-gray-200 flex flex-col z-20 shadow-lg"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-gray-800">Devices</span>
              </div>
              <motion.button whileTap={{ scale: 0.9 }} onClick={() => setDevicePaletteOpen(false)}
                className="p-1 rounded-md hover:bg-gray-100 text-gray-400 transition-colors">
                <X className="w-4 h-4" />
              </motion.button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search devices..."
                className="w-full text-xs pl-8 pr-3 py-2 rounded-md border border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none transition-colors"
              />
            </div>

            {selectedInfrastructure && (
              <div className="mt-2 text-[10px] text-blue-600 bg-blue-50 rounded-md px-2 py-1.5">
                Drag or click to link to: <strong>{selectedInfrastructure.name}</strong>
              </div>
            )}
          </div>

          {/* Device list */}
          <div className="flex-1 overflow-y-auto px-2 py-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
              </div>
            ) : Object.keys(grouped).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Cpu className="w-10 h-10 text-gray-200 mb-3" />
                <p className="text-sm font-medium text-gray-400">No devices found</p>
                <p className="text-xs text-gray-300 mt-1">Register devices in IoT Shack first</p>
              </div>
            ) : (
              Object.entries(grouped).map(([type, devs]) => (
                <div key={type} className="mb-2">
                  <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider px-2 py-1">
                    {type} <span className="text-gray-300">({devs!.length})</span>
                  </div>
                  {devs!.map(d => (
                    <DeviceItem key={d.deviceId} device={d} onLink={handleLink} />
                  ))}
                </div>
              ))
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
